import { Link } from "react-router-dom";
import {
  centsUSD,
  productDataLabel,
  productDurationLabel,
  type Product,
} from "../lib/api";
import {
  countryDisplayName,
  isMultiGeo,
  primaryFlagCode,
} from "../lib/flags";
import { haptic } from "../lib/telegram";
import { useI18n } from "../lib/i18n";
import { Flag, ProductFlag } from "./Flag";

/** 套餐列表卡：国旗 + 国家名 + 规格 + 价格 */
export function PackageCard({
  product,
  countryHint,
}: {
  product: Product;
  countryHint?: string;
}) {
  const { t, locale } = useI18n();
  const data = productDataLabel(product);
  const days = productDurationLabel(product, t("days"));
  const title = product.name || product.title || product.slug;
  const geos = product.geo_codes || product.countries || [];
  const multi = isMultiGeo(product.geo_type, geos);
  const flagCode =
    primaryFlagCode(geos, product.geo_type, countryHint) ||
    (countryHint ? countryHint : geos[0] || null);

  const countryLabel = (() => {
    if (countryHint) return countryDisplayName(countryHint, locale);
    if (!multi && geos[0]) return countryDisplayName(geos[0], locale);
    if (product.geo_type === "GLOBAL" || /global/i.test(title || "")) {
      return t("geoGlobal");
    }
    if (multi) {
      const n = geos.length;
      if (n > 1) return t("geoMultiCount", { n });
      return t("filterScopeMulti");
    }
    return t("dataPlan");
  })();

  return (
    <Link
      to={`/product/${encodeURIComponent(product.slug || String(product.id))}`}
      className="pkg-card"
      onClick={() => haptic("select")}
    >
      {/* 顶行：国旗 + 国家名 */}
      <div className="pkg-country-row">
        {flagCode && !multi ? (
          <Flag code={flagCode} size={28} height={20} shape="rounded" />
        ) : (
          <ProductFlag
            geoCodes={geos}
            geoType={product.geo_type}
            countryHint={countryHint}
            size={28}
          />
        )}
        <span className="pkg-country-name">{countryLabel}</span>
        {flagCode && !multi ? (
          <span className="pkg-country-iso">{String(flagCode).toUpperCase()}</span>
        ) : null}
      </div>

      <div className="pkg-body">
        <div className="pkg-body-main">
          <div className="pkg-title">{title}</div>
          <div className="pkg-meta">
            {data}
            {days ? ` · ${days}` : ""}
          </div>
          <div className="pkg-chips">
            <span className="pkg-chip">{data}</span>
            {days ? <span className="pkg-chip soft">{days}</span> : null}
          </div>
        </div>
        <div className="pkg-price-col">
          <div className="price pkg-price">{centsUSD(product.retail_price_cents || 0)}</div>
        </div>
      </div>
    </Link>
  );
}
